import { useQuery } from '@tanstack/react-query';
import { HardDrive } from 'lucide-react';
import { apiClient, type ApiError, type StorageUsageBreakdownItem } from '@/lib/api-client';
import { formatBytes } from '@/lib/format-bytes';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

/**
 * Storage S3 + quotas (rodada 3b) — consumo de armazenamento do escritorio.
 *
 * Consome GET /storage/usage (tenant-scoped). Diferente do gerenciador de
 * disco do admin, esta tela NUNCA menciona a fonte da quota (override /
 * plano / padrao) — e detalhe interno da cascata, sem valor para o
 * escritorio (ver storage-usage.controller.ts no backend).
 */

const RESOURCE_TYPE_LABEL: Record<string, string> = {
  client_attachments: 'Anexos de clientes',
  task_attachments: 'Anexos de tarefas',
  financial_attachments: 'Anexos financeiros',
  task_comment_attachments: 'Anexos de comentários',
  tribunal_documents: 'Documentos de tribunais',
  portal_uploads: 'Envios do portal',
};

function resourceTypeLabel(resourceType: string): string {
  return RESOURCE_TYPE_LABEL[resourceType] ?? resourceType;
}

function usageBarColor(percent: number): string {
  if (percent > 95) return 'bg-red-500';
  if (percent > 80) return 'bg-amber-500';
  return 'bg-[var(--accent)]';
}

function getErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  const apiErr = err as ApiError | undefined;
  if (apiErr?.message) return apiErr.message;
  return 'Erro inesperado. Tente novamente.';
}

export function StoragePage() {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['storage', 'usage'],
    queryFn: () => apiClient.storageUsage(),
  });

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="text-xl font-semibold text-[var(--fg)]">Armazenamento</h1>
        <p className="mt-1 max-w-2xl text-sm text-[var(--fg-muted)]">
          Espaço usado pelos arquivos do escritório (clientes, tarefas, financeiro, tribunais,
          documentos do portal).
        </p>
      </header>

      {isPending ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-[var(--fg-muted)]">Carregando...</p>
          </CardContent>
        </Card>
      ) : isError ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-red-500">
              Não foi possível carregar o consumo de armazenamento agora.{' '}
              {getErrorMessage(error)}
            </p>
          </CardContent>
        </Card>
      ) : !data ? null : (
        <>
          <UsageSummaryCard
            quotaBytes={data.quotaBytes}
            usedBytes={data.usedBytes}
            availableBytes={data.availableBytes}
            usagePercent={data.usagePercent}
          />
          <BreakdownCard breakdown={data.breakdown} />
        </>
      )}
    </div>
  );
}

function UsageSummaryCard({
  quotaBytes,
  usedBytes,
  availableBytes,
  usagePercent,
}: {
  quotaBytes: number;
  usedBytes: number;
  availableBytes: number;
  usagePercent: number;
}) {
  const percent = Math.min(100, Math.max(0, usagePercent));

  return (
    <Card>
      <CardHeader className="flex flex-row items-start gap-3 space-y-0">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] text-[var(--fg-muted)]">
          <HardDrive className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-[var(--fg)]">Uso do espaço</h2>
          <p className="mt-0.5 text-sm text-[var(--fg-muted)]">
            {formatBytes(usedBytes)} de {formatBytes(quotaBytes)} usados
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-[color-mix(in_oklch,var(--fg)_10%,transparent)]">
          <div
            className={`h-full rounded-full transition-all ${usageBarColor(usagePercent)}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-[var(--fg-muted)]">
          <span>
            {usagePercent.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% usado
          </span>
          <span>{formatBytes(availableBytes)} disponível</span>
        </div>
      </CardContent>
    </Card>
  );
}

function BreakdownCard({ breakdown }: { breakdown: StorageUsageBreakdownItem[] }) {
  return (
    <Card>
      <CardHeader>
        <h2 className="text-sm font-semibold text-[var(--fg)]">Uso por tipo de arquivo</h2>
      </CardHeader>
      <CardContent className="pt-0">
        {breakdown.length === 0 ? (
          <p className="text-sm text-[var(--fg-muted)]">
            Nenhum arquivo armazenado ainda.
          </p>
        ) : (
          <ul className="divide-y divide-[var(--border)]">
            {breakdown
              .slice()
              .sort((a, b) => b.usedBytes - a.usedBytes)
              .map((row) => (
                <li
                  key={row.resourceType}
                  className="flex items-center justify-between gap-4 py-2.5 text-sm"
                >
                  <span className="text-[var(--fg)]">{resourceTypeLabel(row.resourceType)}</span>
                  <span className="flex items-center gap-2 text-[var(--fg-muted)]">
                    <span>
                      {row.count} arquivo{row.count === 1 ? '' : 's'}
                    </span>
                    <span className="font-medium text-[var(--fg)]">
                      {formatBytes(row.usedBytes)}
                    </span>
                  </span>
                </li>
              ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
